import React, {useContext, useEffect, useRef, useState} from 'react';
import ConfirmDialogTemplate from '@/Components/Templates/ConfirmDialogTemplate';
import {TabPanel, TabView} from 'primereact/tabview';
import {Accordion, AccordionTab} from 'primereact/accordion';
import {caseCustomTabs, caseFormConfig, fieldsPlaceholders} from '@/Config/forms';
import {Button} from 'primereact/button';
import TextInputTemplate from '@/Components/Templates/Forms/TextInputTemplate';
import DropdownInputTemplate from '@/Components/Templates/Forms/DropdownInputTemplate';
import {getFormErrorMessage, isFormFieldValid, validateForm} from '@/Helpers/forms';
import DateInputTemplate from '@/Components/Templates/Forms/DateInputTemplate';
import NumberInputTemplate from '@/Components/Templates/Forms/NumberInputTemplate';
import CustomSelectTemplate from '@/Components/Templates/Forms/CustomSelectTemplate';
import ContentLoadingTemplate from '@/Components/Templates/ContentLoadingTemplate';
import {apiRequest, formatBigNo, parseDataFromAPI, toast} from '@/Helpers/general';
import {AppContext} from '@/Components/Context/AppContext';
import Notes from '@/Components/Partials/Tabs/Notes';
import Documents from '@/Components/Partials/Tabs/Documents';
import Audit from '@/Components/Partials/Tabs/Audit';
import ClaimsSummary from '@/Components/Partials/Tabs/ClaimsSummary';
import OCR from '@/Components/Partials/Tabs/OCR';
import moment from 'moment';
import Rulesets from '@/Components/Partials/Tabs/Rulesets';
import {classNames} from 'primereact/utils';
import Outcomes from '@/Components/Partials/Tabs/Outcomes';
import {CaseContext} from '@/Components/Context/CaseContext';
import {RequestsContext} from '@/Components/Context/RequestsContext';
import TextareaInputTemplate from '@/Components/Templates/Forms/TextareaInputTemplate';
import Isd from '@/Components/Partials/Tabs/Isd';
import {TieredMenu} from 'primereact/tieredmenu';
import {paymentPackTotalsFields} from '@/Config/forms/payment-pack';
import Offers from '@/Components/Partials/Tabs/Offers';
import {ScrollPanel} from 'primereact/scrollpanel';
import GtaRateSelect from "@/Components/Forms/Form/Selector/GtaRateSelect";

/**
 * Component which renders a modal containing details about a case
 */
const CaseModal = ({visible, setVisible, caseId, setCaseId, disabled = false, currentRequest}) => {
    const {toastRef} = useContext(AppContext);
    const {statusOptions, chosOptions, gtaGroupsOptions} = useContext(RequestsContext);
    const modalTabsMenuRef = useRef();
    const modalTabsScrollRef = useRef();
    const modalTabsBtnRef = useRef();
    const booleanOptions = [
        {
            label: 'No',
            value: 0
        },
        {
            label: 'Yes',
            value: 1
        }
    ];
    const dbFields = ['created_at', 'updated_at', 'id', 'case_id']; // ignore when saving data

    const [currentTabName, setCurrentTabName] = useState(caseFormConfig.tabs[currentRequest?.has_ocr_file ? 0 : 1].key);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [activeAccordionIndex, setActiveAccordionIndex] = useState({});
    const [formData, setFormData] = useState();
    const [ocrFile, setOcrFile] = useState();
    const [initialFormData, setInitialFormData] = useState();
    const [formValidationRules, setFormValidationRules] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [accordionErrors, setAccordionErrors] = useState({});
    const [formLoading, setFormLoading] = useState(false);
    const [rulesetsFields, setRulesetsFields] = useState();
    const [dropdownsOptions, setDropdownsOptions] = useState({});
    const [dropdownsOptionsApi, setDropdownsOptionsApi] = useState({});
    const [dropdownsOptionsDependency, setDropdownsOptionsDependency] = useState({});
    const [showTabSwitchConfirm, setShowTabSwitchConfirm] = useState(false);
    const [newTabEvent, setNewTabEvent] = useState();
    const [canSave, setCanSave] = useState(false);
    const [outcomeDays, setOutcomeDays] = useState(0);
    const [outcomeAmount, setOutcomeAmount] = useState(0);
    const [outcomeTotal, setOutcomeTotal] = useState(0);
    const [requestedOutcomeDays, setRequestedOutcomeDays] = useState(0);
    const [requestedTotal, setRequestedTotal] = useState(0);
    const [requestedOutcomeAmount, setRequestedOutcomeAmount] = useState(0);
    const [gtaResponseOutcomeDays, setGtaResponseOutcomeDays] = useState(0);
    const [gtaResponseOutcomeAmount, setGtaResponseOutcomeAmount] = useState(0);
    const [gtaResponseOutcomeTotal, setGtaResponseOutcomeTotal] = useState(0);
    const [caseData, setCaseData] = useState();
    const [modalTabs, setModalTabs] = useState([]);
    const [visibleDateRanges, setVisibleDateRanges] = useState({});
    const [updateCurrentTabInHMenu, setUpdateCurrentTabInHMenu] = useState(Date.now());
    const [triggerSaveOffer, setTriggerSaveOffer] = useState();
    const [visibleFieldGroups, setVisibleFieldGroups] = useState();
    const [refreshBtnLoading, setRefreshBtnLoading] = useState(false);
    const [switchToTab, setSwitchToTab] = useState();
    const [initialRulesetsFormData, setInitialRulesetsFormData] = useState();
    const [currentRulesetsFormData, setCurrentRulesetsFormData] = useState();
    const [saveRulesCategories, setSaveRulesCategories] = useState();

    /**
     * Set which date ranges or fields group should be visible
     *
     * @param {object} data form data object
     */
    const setConfigVisibleFields = (data) => {
        let copyVisibleDateRanges = {...visibleDateRanges},
            copyVisibleFieldGroups = {...visibleFieldGroups},
            dateRanges = {},
            fieldGroups = {};

        caseFormConfig.tabs.forEach((tab) => {
            tab.fields.forEach((category) => {
                category.fields.forEach((field) => {
                    if (field.type == 'date_range_start') {
                        // here we will save the index of the last visible date range from the group
                        if (data[tab.key][field.name]) {
                            dateRanges[field.dateRange.key] = field.dateRange.index;
                        }
                    }

                    if (field.grouppedFields) {
                        // here we will save the index of the last fields group who has data in any of it's fields
                        if (data[tab.key][field.name]) {
                            fieldGroups[field.grouppedFields.key] = field.grouppedFields.index;
                        }
                    }
                });
            });
        });

        for (const key in copyVisibleDateRanges) {
            if (Object.hasOwnProperty.call(copyVisibleDateRanges, key)) {
                if (dateRanges[key]) {
                    copyVisibleDateRanges[key] = dateRanges[key];
                }
            }
        }

        for (const key in copyVisibleFieldGroups) {
            if (Object.hasOwnProperty.call(copyVisibleFieldGroups, key)) {
                if (fieldGroups[key]) {
                    copyVisibleFieldGroups[key] = fieldGroups[key];
                }
            }
        }
        // console.log(copyVisibleFieldGroups);

        setVisibleDateRanges(copyVisibleDateRanges);
        setVisibleFieldGroups(copyVisibleFieldGroups);
    };

    /**
     * Get case data
     *
     * @param {object} options optional object containing different options
     */
    const getCaseData = (options) => {
        setRefreshBtnLoading(true);

        apiRequest('get', 'case/' + caseId, {
            data: {},
            onSuccess: (response) => {
                setRefreshBtnLoading(false);

                if (response.result) {
                    setCaseData(response.result);

                    // set form data
                    let copyFormData = {...formData},
                        copyInitialFormData = {...initialFormData};

                    caseFormConfig.tabs.forEach((tab) => {
                        if (response.result[tab.key]) {
                            copyFormData[tab.key] = parseDataFromAPI(response.result[tab.key], tab.key);
                            copyInitialFormData[tab.key] = parseDataFromAPI(response.result[tab.key], tab.key);
                        }
                    });
                    setConfigVisibleFields(copyFormData);
                    setOcrFile(response.result?.ocr_file);
                    setFormData(copyFormData);
                    setInitialFormData(copyInitialFormData);

                    if (response.result?.fields_in_rules_by_relation) {
                        setRulesetsFields(response.result?.fields_in_rules_by_relation);
                    }

                    if (options?.switchToTab == 'ocr') {
                        setSwitchToTab('ocr');
                    }

                    // if (copyFormData?.offers_payments?.total_hire_period_offered) {
                    //     setOutcomeDays(copyFormData.offers_payments.total_hire_period_offered);
                    // }

                    // if (copyFormData?.offers_payments?.hire_daily_rate_offered) {
                    //     setOutcomeAmount(copyFormData.offers_payments.hire_daily_rate_offered);
                    // }
                }

                setFormLoading(false);
            },
            onFailure: () => {
                setRefreshBtnLoading(false);
                setFormLoading(false);
            }
        });
    };

    /**
     * Update the state of the form data (when changing an input value)
     *
     * @param {string} name the name of the input
     * @param {string} value the new value of the input
     * @param {string} category from where the input belongs to
     * @param integer
     */
    const changeFormData = (name, value, category, integer = false) => {
        let getFormData = {...formData},
            getName = name.replace(category + '-', ''),
            fieldsConfig = caseFormConfig.tabs.find(tab => tab.key == currentTabName)?.fields,
            allFields = [];

        fieldsConfig.forEach((config) => {
            allFields = allFields.concat(config.fields);
        });

        let fieldConfig = allFields.find(field => field.name == getName);

        if (fieldConfig) {
            // check if this field is a dependency source
            if (fieldConfig.dependencySource && value != fieldConfig.dependencyMatchValue) {
                allFields.forEach((field) => {
                    // if dependent fields will be hidden, clear their values
                    if (field.dependency && field.dependency.type == 'visibility' && field.dependency.sourceField == getName) {
                        changeFormData(category + '-' + field.name, '', category);
                    }
                });
            }

            if (value === null) {
                let totalDays = 0;
                if (fieldConfig.type == 'date_range_start' && fieldConfig.calculate?.type == 'days') {
                    getFormData[category][fieldConfig.calculate?.targetField] = 0
                    allFields.forEach((item) => {
                        if (item.name != fieldConfig.calculate?.targetField
                            && item?.calculate?.group == fieldConfig?.dateRange?.key
                            && getFormData[category][item.name]) {
                            totalDays += parseInt(getFormData[category][item.name]);
                        }
                    });

                    getFormData[category][fieldConfig.calculate?.totalField] = totalDays;
                }
            }
            if (fieldConfig.calculate) {
                // check if this fields is a date range and calculate the number of days selected
                if (fieldConfig.type == 'date_range_start' && fieldConfig.calculate?.type == 'days') {
                    if (Array.isArray(value) && value[0] && value[1]) {
                        let startDate = moment(value[0]),
                            endDate = moment(value[1]),
                            daysBetween,
                            totalDays = 0;

                        if (startDate.isValid() && endDate.isValid()) {
                            daysBetween = startDate.diff(endDate, 'days');
                        }

                        if ((daysBetween || daysBetween === 0) && fieldConfig.calculate?.targetField) {
                            getFormData[category][fieldConfig.calculate?.targetField] = Math.abs(daysBetween) + 1;

                            totalDays += Math.abs(daysBetween) + 1;
                        }

                        // go thru each number of days field and calculate the total
                        allFields.forEach((item) => {
                            if (item.name != fieldConfig.calculate?.targetField
                                && item?.calculate?.group == fieldConfig?.dateRange?.key
                                && getFormData[category][item.name]) {
                                totalDays += parseInt(getFormData[category][item.name]);
                            }
                        });

                        getFormData[category][fieldConfig.calculate?.totalField] = totalDays;
                    }
                }

                // // check if this is a number of days field and update the total amount of days field
                // if (fieldConfig.calculate?.type == 'days' && fieldConfig.calculate?.group) {
                //     // go thru each number of days field and update the total
                //     let totalDaysNo = 0;

                //     allFields.forEach((fieldItem) => {
                //         if (fieldItem.name == fieldConfig.name) {
                //             totalDaysNo += value;

                //         } else {
                //             if (fieldItem?.calculate?.group == fieldConfig?.calculate?.group
                //                 && getFormData[category][fieldItem.name]) {
                //                 totalDaysNo += parseInt(getFormData[category][fieldItem.name]);
                //             }
                //         }
                //     });

                //     getFormData[category][fieldConfig.calculate?.totalField] = totalDaysNo;
                // }
            }

            // check if this field is a dropdown which must generate dropdown options for another field
            if (fieldConfig.dropdownOptionsDependFor) {
                if (fieldConfig?.dropdownOptionsAPI == 'gtaGroups') {
                    apiRequest('get', 'gtagroups', {
                        data: {
                            filters: {
                                name: value,
                                group_by: 'rate'
                            }
                        },
                        onSuccess: (response) => {
                            let copyDdlOptions = {...dropdownsOptionsDependency},
                                newOptions = [{
                                    label: 'Manual Input...',
                                    value: 'manual-input',
                                },];

                            if (response.result) {
                                response.result.forEach((item) => {
                                    newOptions.push({
                                        label: item.rate,
                                        value: item.rate
                                    });
                                });

                                copyDdlOptions[fieldConfig.dropdownOptionsDependFor] = newOptions;

                            } else {
                                copyDdlOptions[fieldConfig.dropdownOptionsDependFor] = [];
                            }

                            setDropdownsOptionsDependency(copyDdlOptions);
                        },
                        onFailure: () => {
                        }
                    });
                }
            }

            // check if this field should update the value of another field
            if (fieldConfig.changesAnotherFieldValue) {
                let compareField = getFormData[fieldConfig.changesAnotherFieldValue.compareField.category][fieldConfig.changesAnotherFieldValue.compareField.field];

                if (value && compareField) {
                    // check date after
                    if (fieldConfig.changesAnotherFieldValue.compare.type == 'dateAfter') {
                        getFormData[category][fieldConfig.changesAnotherFieldValue.targetField] = moment(value).isAfter(compareField)
                            ? fieldConfig.changesAnotherFieldValue.compare.trueValue
                            : fieldConfig.changesAnotherFieldValue.compare.falseValue;
                    }

                    // check date before
                    if (fieldConfig.changesAnotherFieldValue.compare.type == 'dateBefore') {
                        getFormData[category][fieldConfig.changesAnotherFieldValue.targetField] = moment(value).isBefore(compareField)
                            ? fieldConfig.changesAnotherFieldValue.compare.trueValue
                            : fieldConfig.changesAnotherFieldValue.compare.falseValue;
                    }

                    // check year after
                    if (fieldConfig.changesAnotherFieldValue.compare.type == 'yearAfter') {
                        getFormData[category][fieldConfig.changesAnotherFieldValue.targetField] = moment(value).isAfter(moment(compareField).add(1, 'years')) ? 0 : 1;
                    }

                    // check year before
                    if (fieldConfig.changesAnotherFieldValue.compare.type == 'yearBefore') {
                        getFormData[category][fieldConfig.changesAnotherFieldValue.targetField] = moment(value).isBefore(moment(compareField).subtract(1, 'years')) ? 0 : 1;
                    }
                }
            }
        }
        // console.log(getFormData);

        if (integer) {
            if (value <= 9999999) {
                getFormData[category][getName] = value;
                setFormData(getFormData);
            }
        } else {
            getFormData[category][getName] = value;
            setFormData(getFormData);
        }
    };

    /**
     * Update the state of the active accordion index (of each tab)
     *
     * @param {string} name the name of the tab
     * @param {string} value the new value of the index
     */
    const changeAccordionIndex = (name, value) => {
        let getAccordionIndex = {...activeAccordionIndex};

        getAccordionIndex[name] = value;

        setActiveAccordionIndex(getAccordionIndex);
    };

    /**
     * Generate a text information message
     *
     * @param {object} tab data about the current selected tab
     * @param {object} field data about a field
     *
     * @return null or a text string
     */
    const generateInfoMsg = (tab, field) => {
        let infoMsg = null;

        if (rulesetsFields && rulesetsFields[tab.key] && rulesetsFields[tab.key][field.name]) {
            let getKeys = Object.keys(rulesetsFields[tab.key][field.name]);

            if (getKeys[0]) {
                if (getKeys.length > 1) {
                    infoMsg = 'This field is used in the "' + getKeys.join(', ') + '" rulesets.';

                } else {
                    infoMsg = 'This field is used in the "' + getKeys[0] + '" ruleset.';
                }
                infoMsg += field.label.includes('Mileage') ? '\nValue is to be entered in Miles.' : ''
            }
        }

        return infoMsg;
    };

    /**
     * Make another date range field visible
     *
     * @param {object} field object with field data
     */
    const addDateRange = (field) => {
        let copyVisibleDateRanges = {...visibleDateRanges};

        copyVisibleDateRanges[field.dateRange.key] = field.dateRange.index;

        setVisibleDateRanges(copyVisibleDateRanges);
    };

    /**
     * Make another fields group visible
     *
     * @param {object} field object with field data
     */
    const addFieldsGroup = (field) => {
        let copyVisibleFieldGroups = {...visibleFieldGroups};

        copyVisibleFieldGroups[field.grouppedFields.key] = field.grouppedFields.index;

        setVisibleFieldGroups(copyVisibleFieldGroups);
    };

    const generateInputField = (tab, field) => {
        let infoMsg = generateInfoMsg(tab, field);
        if (field?.grouppedFields?.firstInGroup && field.grouppedFields.index == visibleFieldGroups[field.grouppedFields.key] + 1) {
            return (
                <div className="mb-3 relative">
                    <label htmlFor={field.name + '-add-btn'} className="w-full block mb-2 pr-4 one-line">&nbsp;</label>
                    <Button className="grey-nav-btn grey-nav-btn-hover" label={field.grouppedFields.addButtonLabel}
                            onClick={() => addFieldsGroup(field)} icon="pi pi-plus"/>
                </div>
            );

        } else {
            switch (field.type) {
                case 'text':
                    return (
                        <TextInputTemplate
                            name={tab.key + '-' + field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.text}
                            labelClassName="one-line"
                            value={formData && formData[tab.key] ? (formData[tab.key][field.name] || '') : ''}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, tab.key + '-' + field.name)}
                            errorMessage={getFormErrorMessage(formErrors, tab.key + '-' + field.name)}
                            category={tab.key}
                            infoMessage={infoMsg}
                            disabled={disabled || field.disabled}
                        />
                    );

                case 'textarea':
                    return (
                        <TextareaInputTemplate
                            name={tab.key + '-' + field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.text}
                            value={formData && formData[tab.key] ? (formData[tab.key][field.name] || '') : ''}
                            bottomMargin={2}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, tab.key + '-' + field.name)}
                            errorMessage={getFormErrorMessage(formErrors, tab.key + '-' + field.name)}
                            category={tab.key}
                            infoMessage={infoMsg}
                            disabled={disabled || field.disabled}
                        />
                    );

                case 'date':
                    return (
                        <DateInputTemplate
                            name={tab.key + '-' + field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.date}
                            labelClassName="one-line"
                            value={formData && formData[tab.key] ? (formData[tab.key][field.name] || '') : ''}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, tab.key + '-' + field.name)}
                            errorMessage={getFormErrorMessage(formErrors, tab.key + '-' + field.name)}
                            category={tab.key}
                            infoMessage={infoMsg}
                            disabled={disabled || field.disabled}
                        />
                    );

                case 'date_range_start':
                    return (<>
                        {(field.dateRange.index == 0
                            || (field.dateRange.index <= visibleDateRanges[field.dateRange.key])
                            || (formData && formData[tab.key][field.name])) && <DateInputTemplate
                            name={tab.key + '-' + field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.dateRange}
                            labelClassName="one-line"
                            value={formData && formData[tab.key] ? (formData[tab.key][field.name] || '') : ''}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, tab.key + '-' + field.name)}
                            errorMessage={getFormErrorMessage(formErrors, tab.key + '-' + field.name)}
                            category={tab.key}
                            infoMessage={infoMsg}
                            disabled={disabled || field.disabled}
                            selectionMode="range"
                        />}
                        {field.dateRange.index == visibleDateRanges[field.dateRange.key] + 1 &&
                            <div className="mb-3 relative">
                                <label htmlFor={field.name + '-add-btn'}
                                       className="w-full block mb-2 pr-4 one-line">&nbsp;</label>
                                <Button className="grey-nav-btn grey-nav-btn-hover" label="Add date range"
                                        onClick={() => addDateRange(field)} icon="pi pi-plus"/>
                            </div>}
                    </>);
                    break;

                case 'boolean':
                case 'dropdown':
                    return (
                        <DropdownInputTemplate
                            name={tab.key + '-' + field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.boolean}
                            labelClassName="one-line"
                            value={formData && formData[tab.key] ? formData[tab.key][field.name] : ''}
                            options={field.type == 'dropdown'
                                ?
                                (field.dropdownOptionsAPI && dropdownsOptionsApi && dropdownsOptionsApi[field.dropdownOptionsAPI]
                                    ? dropdownsOptionsApi[field.dropdownOptionsAPI]
                                    : (field.dropdownOptions && dropdownsOptions && dropdownsOptions[field.name]
                                        ? dropdownsOptions[field.name]
                                        : []))
                                : booleanOptions}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, tab.key + '-' + field.name)}
                            errorMessage={getFormErrorMessage(formErrors, tab.key + '-' + field.name)}
                            category={tab.key}
                            infoMessage={infoMsg}
                            disabled={disabled || field.disabled || (field.name == 'cho_id' && initialFormData && initialFormData[currentTabName]['cho_id'] != '')}
                            filter={field?.dropdownOptionsAPI == 'gtaGroups'}
                        />
                    );

                case 'integer':
                    return (
                        <NumberInputTemplate
                            name={tab.key + '-' + field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.integer}
                            labelClassName="one-line"
                            value={formData && formData[tab.key] ? (formData[tab.key][field.name] || null) : null}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, tab.key + '-' + field.name)}
                            errorMessage={getFormErrorMessage(formErrors, tab.key + '-' + field.name)}
                            category={tab.key}
                            infoMessage={infoMsg}
                            disabled={disabled || field.disabled}
                        />
                    );

                case 'float':
                    return (
                        <NumberInputTemplate
                            name={tab.key + '-' + field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.integer}
                            labelClassName="one-line"
                            value={formData && formData[tab.key] ? (formData[tab.key][field.name] || null) : null}
                            mode={'decimal'}
                            bottomMargin={3}
                            format
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, tab.key + '-' + field.name)}
                            errorMessage={getFormErrorMessage(formErrors, tab.key + '-' + field.name)}
                            category={tab.key}
                            infoMessage={infoMsg}
                            disabled={disabled || field.disabled}
                        />
                    );

                case 'custom-select':
                    return (
                        <CustomSelectTemplate
                            name={tab.key + '-' + field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.boolean}
                            labelClassName="one-line"
                            value={formData && formData[tab.key] ? formData[tab.key][field.name] : ''}
                            options={field.dropdownOptionsDependOn && dropdownsOptionsDependency && dropdownsOptionsDependency[field.name]
                                ? dropdownsOptionsDependency[field.name]
                                : field.customOptions}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, tab.key + '-' + field.name)}
                            errorMessage={getFormErrorMessage(formErrors, tab.key + '-' + field.name)}
                            category={tab.key}
                            infoMessage={infoMsg}
                            disabled={disabled || field.disabled}
                        />
                    );

                case 'link':
                    return (
                        <TextInputTemplate
                            name={tab.key + '-' + field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.link}
                            labelClassName="one-line"
                            value={formData && formData[tab.key] ? (formData[tab.key][field.name] || '') : ''}
                            bottomMargin={3}
                            clickable={true}
                            disabled={true}
                        />
                    );

                case 'gta_rate':
                    return <GtaRateSelect
                        name={tab.key + '-' + field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.dropdown}
                        value={formData && formData[tab.key] ? (formData[tab.key][field.name] || '') : ''}
                        errorMessages={getFormErrorMessage(formErrors, tab.key + '-' + field.name)}
                        pt={{dropdown: {className: 'w-full'}}}
                        onChange={(e) => changeFormData(tab.key + '-' + field.name, e.value, tab.key)}
                    />

                default:
                    break;
            }
        }

    };

    /**
     * Accordion header
     *
     * @param {object} category object with category data
     */
    const accordionHeader = (category) => {
        let errorsNo = accordionErrors && accordionErrors[currentTabName][category.key] ? accordionErrors[currentTabName][category.key] : 0;

        return (
            <div className="flex justify-content-between w-full">
                <span className="p-accordion-header-text">{category.name}</span>
                {errorsNo > 0 &&
                    <span className="p-error">{accordionErrors[currentTabName][category.key]} errors</span>}
            </div>
        );
    };

    /**
     * Generate total amounts info (for payment pack top section)
     */
    const generateTotalAmounts = () => {
        let totalAmounts = [],
            fieldsConfig = caseFormConfig.tabs.find(tab => tab.key == 'payment_pack')?.fields,
            allFields = [];

        fieldsConfig.forEach((config) => {
            allFields = allFields.concat(config.fields);
        });

        allFields.forEach((field) => {
            if (paymentPackTotalsFields.includes(field.name)) {
                // console.log(field.name);
                totalAmounts.push({
                    label: field.label,
                    amount: formData['payment_pack'][field.name]
                });
            }
        });

        return (
            <div className="grid">
                <div className="col-3"></div>
                <div className="col-6">
                    {totalAmounts.map((item, index) => {
                        return (<div key={item.label}>
                            {index == totalAmounts.length - 1 && <div className="grid">
                                <div className="col-12">
                                    <hr/>
                                </div>
                            </div>}
                            <div className="grid">
                                <div className="col-3 text-right">
                                    <span><span
                                        style={{color: 'var(--gray-500)'}}>£</span> {item.amount ? formatBigNo(item.amount) : 0}</span>
                                </div>

                                <div className="col-9">
                                    <span className="font-semibold">{item.label}</span>
                                </div>
                            </div>
                        </div>);
                    })}
                </div>
            </div>
        );
    };

    /**
     * Check if a category accordion can be shown or not
     *
     * @param {object} category object with category data
     */
    const canShowCategory = (category) => {
        return !category.dependsOn || (category.dependsOn && formData && formData[category.dependsOn.category][category.dependsOn.key] == category.dependsOn.compareValue);
    };

    /**
     * Check if a field wrapper and it's contents can be shown or not
     *
     * @param {object} tab object with tab data
     * @param {object} fields object with field data
     */
    const canShowField = (tab, field) => {
        return field.type != 'date_range_end' && field.type != 'date_range_start' && !field.group && !field.grouppedFields && !field.dependency // all other field types
            // fields which are date range
            || (field.type == 'date_range_start'
                && (field.dateRange.index <= parseInt(visibleDateRanges[field.dateRange.key]) + 1 || (formData && formData[tab.key][field.name]))
            )
            // fields which are in the same date range group
            || (field.group
                && (field.group.index <= visibleDateRanges[field.group.key])
            )
            // fields which are in the same group but do not depend on other field's value
            || (field.grouppedFields && !field.dependency
                && ((field.grouppedFields.index <= visibleFieldGroups[field.grouppedFields.key]) || (field.grouppedFields.index <= visibleFieldGroups[field.grouppedFields.key] + 1 && field.grouppedFields.firstInGroup))
            )
            // fields which depend on other field's value but are not in the same group
            || (field.dependency && field.dependency.type == 'visibility' && !field.grouppedFields
                && formData && formData[tab.key][field.dependency.sourceField] === field.dependency.matchValue
            )
            // fields which are in the same group and depend on other field's value
            || (field.grouppedFields
                && field.dependency && field.dependency.type == 'visibility'
                && ((field.grouppedFields.index <= visibleFieldGroups[field.grouppedFields.key]) || (field.grouppedFields.index <= visibleFieldGroups[field.grouppedFields.key] + 1 && field.grouppedFields.firstInGroup))
                && formData && formData[tab.key][field.dependency.sourceField] === field.dependency.matchValue
            );
    };

    /**
     * Generate a tab form
     *
     * @param {object} tab config data
     */
    const generateTabForm = (tab) => {
        return (<>
            {tab.key == 'payment_pack' && generateTotalAmounts()}

            <div className="general-form relative">
                <Accordion activeIndex={activeAccordionIndex[currentTabName]}
                           onTabChange={(e) => changeAccordionIndex(currentTabName, e.index)} className="my-5">
                    {tab.fields.map((category) => {
                        if (canShowCategory(category)) {
                            return (
                                <AccordionTab headerTemplate={() => accordionHeader(category)} key={category.key}>
                                    <div className="formgrid grid my-3">
                                        <div className="col-12">
                                            <div className="grid">
                                                {category.fields.map((field) => {
                                                    if (canShowField(tab, field)) {
                                                        return (
                                                            <div className="field col-12 md:col-6 lg:col-4"
                                                                 key={field.name}>
                                                                {generateInputField(tab, field)}
                                                            </div>
                                                        );
                                                    }
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </AccordionTab>
                            );
                        }
                    })}
                </Accordion>

                {formLoading && <ContentLoadingTemplate/>}
            </div>
        </>);
    };

    /**
     * Generate the content of a  tab
     *
     * @param {object} tab config data
     */
    const generateTabContent = (tab) => {
        switch (tab.key) {
            case 'ocr':
                return (
                    <div className="custom-content">
                        <span>
                            <OCR formLoading={formLoading} currentRequest={currentRequest}/>
                        </span>
                        {formLoading && <ContentLoadingTemplate/>}
                    </div>
                );

                break;

            case 'claim_summary':
                return (
                    <div className="custom-content">
                        <span>
                            <ClaimsSummary tab={tab} formData={formData} formLoading={formLoading}
                                           request={caseData.request} reloadCaseData={getCaseData}/>
                        </span>
                        {formLoading && <ContentLoadingTemplate/>}
                    </div>
                );
                break;

            case 'documents':
                return (
                    <div className="custom-content">
                        <Documents caseId={caseId} caseData={caseData} disabled={disabled}/>
                        {formLoading && <ContentLoadingTemplate/>}
                    </div>
                );
                break;

            case 'notes':
                return (
                    <div className="custom-content">
                        <Notes caseId={caseId} disabled={disabled}/>
                        {formLoading && <ContentLoadingTemplate/>}
                    </div>
                );
                break;

            case 'audits':
                return (
                    <div className="custom-content">
                        <Audit caseId={caseId}/>
                        {formLoading && <ContentLoadingTemplate/>}
                    </div>
                );
                break;

            case 'rulesets':
                return (
                    <div className="custom-content">
                        <Rulesets caseId={caseId} reloadCaseData={getCaseData} disabled={disabled}/>
                        {formLoading && <ContentLoadingTemplate/>}
                    </div>
                );
                break;

            case 'outcomes':
                return (
                    <div className="custom-content">
                        <Outcomes caseId={caseId} reloadCaseData={getCaseData} disabled={disabled}/>
                        {formLoading && <ContentLoadingTemplate/>}
                    </div>
                );
                break;

            case 'isd':
                return (
                    <div className="custom-content">
                        <Isd caseId={caseId} formData={formData} setFormData={setFormData} submitIsd={submitForm}
                             initialFormData={initialFormData} setInitialFormData={setInitialFormData}
                             reloadCaseData={getCaseData} request={caseData.request}/>
                        {formLoading && <ContentLoadingTemplate/>}
                    </div>
                );
                break;

            case 'offers_payments':
                return (
                    <div className="custom-content">
                        <Offers caseId={caseId} triggerSave={triggerSaveOffer}/>
                        {formLoading && <ContentLoadingTemplate/>}
                    </div>
                );
                break;

            default:
                return generateTabForm(tab);
                break;
        }
    };

    /**
     * Change the tab
     *
     * @param {string} newTab the key of the new tab
     */
    const changeCaseTab = (newTab) => {
        let newTabIndex = 0,
            searchTabIndex = modalTabs.findIndex((tab) => tab.key == newTab);

        if (searchTabIndex > -1) {
            newTabIndex = searchTabIndex;
        }

        setActiveTabIndex(newTabIndex);
        setCurrentTabName(modalTabs[newTabIndex].key);
        setUpdateCurrentTabInHMenu(Date.now());
    };

    const modalContent = (
        <div className="modal-wrap">
            <TabView
                activeIndex={activeTabIndex}
                onTabChange={(e) => {
                    let modifiedData = getModifiedData(),
                        checkRulesets = false;

                    if (currentTabName == 'rulesets') {
                        let getModifiedRules = getModifiedRulesetsData();

                        checkRulesets = getModifiedRules.hasModifiedData;
                        e.modifiedRules = getModifiedRules.modifiedRules;
                    }

                    if ((modifiedData && Object.keys(modifiedData).length > 0) || checkRulesets) {
                        setShowTabSwitchConfirm(true);
                        setNewTabEvent(e);

                    } else {
                        setActiveTabIndex(e.index);
                        setCurrentTabName(modalTabs[e.index].key);
                        setUpdateCurrentTabInHMenu(Date.now());
                    }
                }}
                scrollable
            >
                {modalTabs.map((tab) => {
                    return (
                        <TabPanel
                            header={tab.label}
                            key={tab.key}
                            className={classNames({
                                'highlighted-tab': tab.highlight,
                                'important-tab': tab.important,
                            })}
                        >
                            {generateTabContent(tab)}
                        </TabPanel>
                    );
                })}
            </TabView>
        </div>
    );

    /**
     * Get only the form data which was modified
     */
    const getModifiedData = () => {
        if (initialFormData) {
            let initialTabData = {...initialFormData[currentTabName]},
                currentTabData = {...formData[currentTabName]},
                modifiedData = {},
                fieldsConfig = caseFormConfig.tabs.find(tab => tab.key == currentTabName)?.fields,
                allFields = [];

            fieldsConfig.forEach((config) => {
                allFields = allFields.concat(config.fields);
            });

            for (let key in initialTabData) {
                let fieldConfig = allFields.find(field => field.name === key);

                if (fieldConfig) {
                    // date ranges
                    if (fieldConfig.type === 'date_range_start') {
                        if (Array.isArray(currentTabData[key])) {
                            let copyDatesArray = [...currentTabData[key]];
                            if (copyDatesArray[0]) {
                                currentTabData[key] = copyDatesArray[0];
                            }

                            if (copyDatesArray[1]) {
                                currentTabData[fieldConfig.endKey] = copyDatesArray[1];
                            }
                        }
                    }

                    // dates
                    if (fieldConfig.type === 'date' || fieldConfig.type === 'date_range_start' || fieldConfig.type === 'date_range_end') {
                        let initialDate = initialTabData[key];

                        if (Array.isArray(initialTabData[key])) {
                            if (fieldConfig.type === 'date_range_start' && initialTabData[key][0]) {
                                initialDate = initialTabData[key][0];
                            }
                        }

                        if (fieldConfig.type === 'date_range_end' && initialTabData[key]) {
                            initialDate = moment(initialTabData[key], 'YYYY-MM-DD HH:mm:ss').toDate();
                        }

                        if (moment.isDate(initialDate) && moment.isDate(currentTabData[key])) {
                            if (
                                fieldConfig.type === "date_range_start" &&
                                moment(initialDate).isSame(currentTabData[key]) &&
                                !moment(initialTabData[fieldConfig.endKey]).isSame(currentTabData[fieldConfig.endKey])
                            ) {
                                modifiedData[key] = currentTabData[key];
                            } else if (!moment(initialDate).isSame(currentTabData[key])) {
                                modifiedData[key] = currentTabData[key];
                            }
                        } else {
                            if (
                                (!moment.isDate(initialDate) && moment.isDate(currentTabData[key])) ||
                                (moment.isDate(initialDate) && !moment.isDate(currentTabData[key]))
                            ) {
                                modifiedData[key] = currentTabData[key];
                            }
                        }

                        if (fieldConfig.type === 'date_range_start') {
                            if (initialTabData[key] != null && currentTabData[key] == null) {
                                modifiedData[fieldConfig.endKey] = null;
                                currentTabData[fieldConfig.endKey] = null;
                            }
                        }
                    } else {
                        if (currentTabData[key] !== initialTabData[key]) {
                            modifiedData[key] = currentTabData[key];
                        }
                    }
                }
            }

            return modifiedData;
        }
    };

    /**
     * Get the rulesets form data which was modified
     */
    const getModifiedRulesetsData = () => {
        // console.log(initialRulesetsFormData, currentRulesetsFormData);
        let hasModifiedData = false,
            modifiedRules = [];

        if (initialRulesetsFormData && currentRulesetsFormData) {
            let initialTabData = {...initialRulesetsFormData},
                currentTabData = {...currentRulesetsFormData},
                modifiedData = {};

            for (let ruleName in initialTabData) {
                modifiedData[ruleName] = {};

                for (let key in initialTabData[ruleName]) {
                    let getTab = key.match(/(.*?)(?=\[)/)[1],
                        getName = key.match(/\[([^[\]]+)\]/)[1],
                        fieldsConfig = caseFormConfig.tabs.find(tab => tab.key == getTab)?.fields,
                        allFields = [];

                    fieldsConfig.forEach((config) => {
                        allFields = allFields.concat(config.fields);
                    });

                    let fieldConfig = allFields.find(field => field.name == getName);

                    if (fieldConfig) {
                        // date ranges
                        if (fieldConfig.type === 'date_range_start') {
                            if (Array.isArray(currentTabData[ruleName][key])) {
                                let copyDatesArray = [...currentTabData[ruleName][key]];

                                if (copyDatesArray[0]) {
                                    currentTabData[ruleName][key] = copyDatesArray[0];
                                }

                                if (copyDatesArray[1]) {
                                    currentTabData[fieldConfig.endKey] = copyDatesArray[1];
                                }
                            }
                        }

                        // dates
                        if (fieldConfig.type === 'date' || fieldConfig.type === 'date_range_start' || fieldConfig.type === 'date_range_end') {
                            let initialDate = initialTabData[ruleName][key];

                            if (Array.isArray(initialTabData[ruleName][key])) {
                                if (fieldConfig.type === 'date_range_start' && initialTabData[ruleName][key][0]) {
                                    initialDate = initialTabData[ruleName][key][0];
                                }
                            }

                            if (fieldConfig.type === 'date_range_end' && initialTabData[ruleName][key]) {
                                initialDate = moment(initialTabData[ruleName][key], 'YYYY-MM-DD HH:mm:ss').toDate();
                            }

                            if (moment.isDate(initialDate) && moment.isDate(currentTabData[ruleName][key])) {
                                if (
                                    fieldConfig.type === "date_range_start" &&
                                    moment(initialDate).isSame(currentTabData[ruleName][key]) &&
                                    !moment(initialTabData[ruleName][getTab + '[' + fieldConfig.endKey + ']']).isSame(currentTabData[fieldConfig.endKey])
                                ) {
                                    modifiedData[ruleName][key] = currentTabData[ruleName][key];
                                    // console.log(initialDate, initialTabData[ruleName][getTab + '[' + fieldConfig.endKey + ']'], currentTabData[ruleName][key], currentTabData[fieldConfig.endKey]);
                                    hasModifiedData = true;
                                    modifiedRules.push(ruleName);

                                } else if (!moment(initialDate).isSame(currentTabData[ruleName][key])) {
                                    modifiedData[ruleName][key] = currentTabData[ruleName][key];
                                    hasModifiedData = true;
                                    modifiedRules.push(ruleName);
                                }

                            } else {
                                if (
                                    fieldConfig.type != 'date_range_end' &&
                                    ((!moment.isDate(initialDate) && moment.isDate(currentTabData[ruleName][key])) ||
                                        (moment.isDate(initialDate) && !moment.isDate(currentTabData[ruleName][key])))
                                ) {
                                    modifiedData[ruleName][key] = currentTabData[ruleName][key];
                                    // console.log(initialDate, currentTabData[ruleName][key], fieldConfig.type);
                                    hasModifiedData = true;
                                    modifiedRules.push(ruleName);
                                }
                            }

                            if (fieldConfig.type === 'date_range_start') {
                                if (initialTabData[ruleName][key] != null && currentTabData[ruleName][key] == null) {
                                    modifiedData[ruleName][fieldConfig.endKey] = null;
                                    currentTabData[ruleName][fieldConfig.endKey] = null;
                                    hasModifiedData = true;
                                    modifiedRules.push(ruleName);
                                }
                            }

                        } else {
                            if (currentTabData[ruleName][key] !== initialTabData[ruleName][key]) {
                                modifiedData[ruleName][key] = currentTabData[ruleName][key];
                                hasModifiedData = true;
                                modifiedRules.push(ruleName);
                            }
                        }
                    }
                }
            }
            // console.log(modifiedData);
        }

        return {
            hasModifiedData: hasModifiedData,
            modifiedRules: modifiedRules
        };
    };

    /**
     * Save the current tab data
     */
    async function submitForm() {
        setFormLoading(true);

        let newTabData = getModifiedData();

        let validation = validateForm(formValidationRules[currentTabName], formData[currentTabName], setFormErrors, currentTabName);

        if (validation) {
            apiRequest(currentTabName == 'isd' ? 'patch' : 'post', 'case/' + caseId + '/' + currentTabName, {
                data: newTabData,
                onSuccess: (response) => {
                    setFormLoading(false);
                    setShowTabSwitchConfirm(false);

                    if (newTabEvent) {
                        setActiveTabIndex(newTabEvent.index);
                        setCurrentTabName(modalTabs[newTabEvent.index].key);
                        setUpdateCurrentTabInHMenu(Date.now());
                        setNewTabEvent();
                    }
                    setFormErrors({});
                    getCaseData();

                    toast(toastRef, 'success', 'Claim data has been updated.');
                },
                onFailure: (error) => {
                    setFormLoading(false);
                    setShowTabSwitchConfirm(false);

                    if (newTabEvent) {
                        setActiveTabIndex(newTabEvent.index);
                        setCurrentTabName(modalTabs[newTabEvent.index].key);
                        setUpdateCurrentTabInHMenu(Date.now());
                        setNewTabEvent();
                    }

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};

                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }

                            setFormErrors(newFormErrors);
                        } else {
                            toast(toastRef, 'error', 'Error saving claim data.');
                        }
                    } else if (error.status == 403) {
                        toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');
                    } else {
                        toast(toastRef, 'error', 'Error saving claim data.');
                    }
                },
            });
        } else {
            setFormLoading(false);
        }
    }

    const widgetFooter = () => {
        // console.log(currentTabName);

        return (
            <>
                {caseCustomTabs.indexOf(currentTabName) === -1 && (
                    <>
                        <hr/>
                        {!disabled && (
                            <div className="text-right">
                                <Button label="Save" onClick={() => {
                                    if (currentTabName == 'offers_payments') {
                                        setTriggerSaveOffer(Date.now());

                                    } else {
                                        submitForm();
                                    }
                                }} loading={formLoading} loadingIcon="pi pi-spin pi-sun" iconPos="center"
                                        disabled={!canSave}/>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    };

    /**
     * Initialize the state for each tab
     */
    const initStateData = () => {
        let tabsAccordionIndexes = {},
            tabsFormData = {},
            tabsInitialFormData = {},
            tabsFormValidationRules = {},
            initialAccordionErrors = {},
            dateRanges = {},
            copyDropdownsOptions = {...dropdownsOptions},
            fieldGroups = {};

        caseFormConfig.tabs.forEach((tab) => {
            tabsAccordionIndexes[tab.key] = 0;
            tabsFormData[tab.key] = {};
            tabsInitialFormData[tab.key] = {};
            tabsFormValidationRules[tab.key] = [];
            initialAccordionErrors[tab.key] = {};

            tab.fields.forEach((category) => {
                initialAccordionErrors[tab.key][category.key] = 0;

                category.fields.forEach((field) => {
                    tabsFormData[tab.key][field.name] = '';
                    tabsInitialFormData[tab.key][field.name] = '';

                    // set validation rules
                    if (field.modalValidation && Object.keys(field.modalValidation).length > 0) {
                        tabsFormValidationRules[tab.key].push({
                            field: field.name,
                            rules: field.modalValidation
                        });
                    }

                    // set dropdown options from the config
                    if (field.dropdownOptions) {
                        copyDropdownsOptions[field.name] = field.dropdownOptions;
                    }

                    // init the visible date range fields
                    if (field.type == 'date_range_start') {
                        if (typeof (dateRanges[field.dateRange.key]) == 'undefined') {
                            dateRanges[field.dateRange.key] = 0; // here we will save the index of the last visible date range from the group
                        }
                    }

                    // init the visible groupped fields
                    if (field.grouppedFields) {
                        if (typeof (fieldGroups[field.grouppedFields.key]) == 'undefined') {
                            fieldGroups[field.grouppedFields.key] = 0; // here we will save the index of the last visible field group from the group
                        }
                    }
                });
            });
        });

        // setDropdownsOptions(copyDropdownsOptions);
        setDropdownsOptions((previousState) => {
            setDropdownsOptions({...previousState, ...copyDropdownsOptions});
        });
        setActiveAccordionIndex(tabsAccordionIndexes);
        setFormData(tabsFormData);
        setInitialFormData(tabsInitialFormData);
        setFormValidationRules(tabsFormValidationRules);
        setAccordionErrors(initialAccordionErrors);
        setVisibleDateRanges(dateRanges);
        setVisibleFieldGroups(fieldGroups);
    };

    /**
     * Jump to the first input element that has an error
     */
    const jumpToFirstError = () => {
        let firstErrorKey = Object.keys(formErrors)[0];

        if (firstErrorKey) {
            let firstInputLabel = document.querySelector('label[for="' + firstErrorKey + '"]');

            if (firstInputLabel) {
                firstInputLabel.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
        }
    };

    useEffect(() => {
        if (visible) {
            // get case
            setFormLoading(true);
            setCurrentTabName(caseFormConfig.tabs[currentRequest?.has_ocr_file ? 0 : 1].key);

            getCaseData();

        } else {
            // reset state data
            // setCurrentTabName(caseFormConfig.tabs[currentRequest?.has_ocr_file ? 0 : 1].key);
            setActiveTabIndex(0);
            setFormErrors({});
            setFormLoading(false);
            initStateData();
            setOcrFile();
        }
    }, [visible]);

    useEffect(() => {
        // enable/disable the save button
        if (formData && formData[currentTabName]) {
            let hasValues = false;

            for (let key in formData[currentTabName]) {
                if (formData[currentTabName][key] !== '' && formData[currentTabName][key] !== null && !dbFields.includes(key)) {
                    hasValues = true;
                }
            }

            setCanSave(hasValues);
        }

    }, [formData, currentTabName]);

    useEffect(() => {
        // set accordion errors
        if (formErrors && accordionErrors && Object.keys(accordionErrors).length > 0) {
            let copyAccordionErrors = {...accordionErrors};

            caseFormConfig.tabs.forEach((tab) => {
                if (tab.key == currentTabName) {
                    tab.fields.forEach((category) => {
                        let categoryErrorsNo = 0;

                        category.fields.forEach((field) => {
                            if (formErrors[tab.key + '-' + field.name]) {
                                categoryErrorsNo++;
                            }
                        });

                        copyAccordionErrors[tab.key][category.key] = categoryErrorsNo;
                    });
                }
            });
            // console.log(copyAccordionErrors);

            setAccordionErrors(copyAccordionErrors);
        }

    }, [formErrors]);

    useEffect(() => {
        if (formErrors && accordionErrors && Object.keys(accordionErrors).length > 0) {
            // open first accordion that has errors
            let firstErrorAccordion,
                firstErrorAccordionIndex = 0;

            for (const tab in accordionErrors) {
                if (tab == currentTabName) {
                    for (const key in accordionErrors[tab]) {
                        if (accordionErrors[tab][key] > 0) {
                            firstErrorAccordion = key;

                            break;
                        }

                        firstErrorAccordionIndex++;
                    }
                }
            }

            if (firstErrorAccordion && activeAccordionIndex[currentTabName] != firstErrorAccordionIndex) {
                changeAccordionIndex(currentTabName, firstErrorAccordionIndex);
            }

            // jump to the first error element
            if (firstErrorAccordion && activeAccordionIndex[currentTabName] != firstErrorAccordionIndex) {
                // wait for change accordion animation transition
                setTimeout(() => {
                    jumpToFirstError();
                }, 1000);

            } else {
                jumpToFirstError();
            }
        }

    }, [accordionErrors]);

    /**
     * Check if a case is rubik and also has ocr file added
     *
     * @param {string} tabKey the name of the tab
     */
    const checkRubikAndOcr = (tabKey) => {
        return tabKey === 'ocr'
            && currentRequest?.case?.is_rubik
            && ['ocr_to_process', 'ocr_in_progress', 'ocr_updating_data', 'ocr_done_ok', 'ocr_done_fail'].includes(caseData?.ocr_file?.status)
    };

    /**
     * Get the case's tabs configuration
     * This is used mostly so we can compare with the correct tab index
     */
    const getCaseTabs = () => {
        let tabs = [],
            index = 0;

        caseFormConfig.tabs.forEach((tab) => {
            if ((tab.key != 'isd' && tab.key != 'ocr')
                || (tab.key == 'isd' && caseData && caseData.is_complete)
                || (tab.key == 'ocr' && currentRequest?.has_ocr_file)
                || checkRubikAndOcr(tab.key)) {
                tabs.push({
                    label: tab.name,
                    key: tab.key,
                    fields: tab.fields,
                    highlight: tab.highlight,
                    value: index
                });

                index++;
            }
        });

        return tabs;
    };

    /**
     * Generate modal tabs
     */
    const generateModalTabs = () => {
        let tabs = [],
            tabsConfig = getCaseTabs(),
            index = 0;

        caseFormConfig.tabs.forEach((tab) => {
            if (!tab.advanced_view_only || (tab.advanced_view_only === true && caseData.request.advanced_view)) {
                if ((tab.key !== 'isd' && tab.key !== 'ocr')
                    || (tab.key === 'isd' && caseData && caseData.is_complete)
                    || (tab.key === 'ocr' && currentRequest?.has_ocr_file)
                    || checkRubikAndOcr(tab.key)) {
                    tabs.push({
                        label: tab.name,
                        key: tab.key,
                        fields: tab.fields,
                        highlight: tab.highlight,
                        value: index,
                        command: (event) => {
                            setActiveTabIndex(event.item.value);
                            setCurrentTabName(tabsConfig[event.item.value].key);
                            setUpdateCurrentTabInHMenu(Date.now());
                        },
                        className: tab.key === currentTabName ? 'p-menuitem-active' : ''
                    });

                    index++;
                }
            }
        });

        setModalTabs(tabs);
    };

    useEffect(() => {
        if (switchToTab) {
            changeCaseTab(switchToTab);
            setSwitchToTab();
        }

    }, [modalTabs]);

    /**
     * Function used to update the current hamburger (tiered) menu item
     */
    const updateHMenu = () => {
        let copyTabs = [...modalTabs];

        if (copyTabs && Array.isArray(copyTabs)) {
            copyTabs.forEach((tab) => {
                tab.className = tab.key == currentTabName ? 'p-menuitem-active' : '';
            });
            // console.log(copyTabs);

            setModalTabs(copyTabs);
        }
    };

    // generate the modal tabs after we have the case data
    useEffect(() => {
        if (caseData) {
            generateModalTabs();
        }

    }, [caseData]);

    // update the current tab in the hamburger (tiered) menu
    useEffect(() => {
        updateHMenu();
    }, [updateCurrentTabInHMenu]);

    useEffect(() => {
        let copyDropdownsOptionsApi = {...dropdownsOptionsApi};

        caseFormConfig.tabs.forEach((tab) => {
            tab.fields.forEach((category) => {
                category.fields.forEach((field) => {
                    // set dropdown options from the API
                    if (chosOptions) {
                        if (field?.dropdownOptionsAPI == 'chos') {
                            copyDropdownsOptionsApi['chos'] = chosOptions;
                        }
                    }

                    if (gtaGroupsOptions) {
                        if (field?.dropdownOptionsAPI == 'gtaGroups') {
                            copyDropdownsOptionsApi['gtaGroups'] = gtaGroupsOptions;
                        }
                    }
                });
            });
        });

        setDropdownsOptionsApi(copyDropdownsOptionsApi);

    }, [chosOptions, gtaGroupsOptions]);

    const modalHeader = (
        <div className="relative">
            <span>
                {formData?.claim_info?.claims_reference_number
                    ? 'Claim no "' + formData.claim_info.claims_reference_number + '" details'
                    : 'Claim details'
                }
            </span>
            {caseData?.request?.advanced_view && <div className='open-modal-tab-btn-wrapper'>
                <Button
                    className="open-modal-tab-btn-none grey-nav-btn grey-nav-btn-hover"
                    icon="pi pi-bars"
                    onClick={(event) => modalTabsMenuRef.current.toggle(event)}
                    ref={modalTabsBtnRef}
                />

                <ScrollPanel style={{display: 'none'}} className="hamburger-scroll" ref={modalTabsScrollRef}>
                    <TieredMenu
                        appendTo="self"
                        className="modal-tabs-menu"
                        model={modalTabs}
                        popup
                        onHide={() => {
                            let hamburgerScroll = modalTabsScrollRef.current.getElement();
                            let button = modalTabsBtnRef.current;
                            // hamburgerScroll.classList.remove("hamburger-scroll");
                            button.classList.remove("open-modal-tab-btn-block");
                            button.classList.add("open-modal-tab-btn-none");
                            hamburgerScroll.style.display = "none";
                        }}
                        onShow={() => {
                            let hamburgerScroll = modalTabsScrollRef.current.getElement();
                            let button = modalTabsBtnRef.current;
                            // hamburgerScroll.classList.add("hamburger-scroll");
                            button.classList.remove("open-modal-tab-btn-none");
                            button.classList.add("open-modal-tab-btn-block");
                            hamburgerScroll.style.display = "block";
                        }}
                        ref={modalTabsMenuRef}
                    />
                </ScrollPanel>

            </div>}
        </div>
    );

    return (
        <CaseContext.Provider value={{
            formData,
            setFormData,
            caseId,
            getCaseData,
            outcomeDays,
            outcomeAmount,
            outcomeTotal,
            setOutcomeDays,
            setOutcomeAmount,
            setOutcomeTotal,
            requestedOutcomeDays,
            setRequestedOutcomeDays,
            requestedOutcomeAmount,
            setRequestedOutcomeAmount,
            requestedTotal,
            setRequestedTotal,
            gtaResponseOutcomeAmount,
            setGtaResponseOutcomeAmount,
            gtaResponseOutcomeDays,
            setGtaResponseOutcomeDays,
            gtaResponseOutcomeTotal,
            setGtaResponseOutcomeTotal,
            caseData,
            changeCaseTab,
            refreshBtnLoading,
            ocrFile,
            setInitialRulesetsFormData,
            setCurrentRulesetsFormData,
            saveRulesCategories,
            setShowTabSwitchConfirm,
            newTabEvent,
            setNewTabEvent,
            setActiveTabIndex,
            setCurrentTabName,
            setUpdateCurrentTabInHMenu,
            modalTabs
        }}>
            <ConfirmDialogTemplate
                title={modalHeader}
                className="modal-tabs"
                message={modalContent}
                onConfirm={() => {
                }}
                visible={visible}
                setVisible={setVisible}
                onHide={() => {
                    setVisible(false);
                    setCaseId();
                }}
                footer={widgetFooter}
                noIcon
            />

            <ConfirmDialogTemplate title="Confirm tab switch"
                                   message={'You have unsaved changes.'}
                                   onConfirm={() => {
                                   }}
                                   visible={showTabSwitchConfirm}
                                   setVisible={setShowTabSwitchConfirm}
                                   footer={(
                                       <>
                                           <Button label="Save changes" className="p-button-outlined mr-3"
                                                   onClick={() => {
                                                       if (newTabEvent?.modifiedRules?.length > 0) {
                                                           setSaveRulesCategories(newTabEvent.modifiedRules);

                                                       } else {
                                                           submitForm();
                                                       }
                                                   }}
                                                   loading={formLoading}
                                                   loadingIcon="pi pi-spin pi-sun"
                                                   iconPos="center"
                                           />
                                           <Button label="Ignore" className="p-button-outlined p-button-secondary"
                                                   onClick={() => {
                                                       if (newTabEvent) {
                                                           setActiveTabIndex(newTabEvent.index);
                                                           // setCurrentTabName(caseFormConfig.tabs[newTabEvent.index].key);
                                                           setCurrentTabName(modalTabs[newTabEvent.index].key);
                                                           setUpdateCurrentTabInHMenu(Date.now());
                                                           setNewTabEvent();
                                                           getCaseData();
                                                       }

                                                       setShowTabSwitchConfirm(false);
                                                   }}/>
                                       </>
                                   )}
            />
        </CaseContext.Provider>
    );
};

export default CaseModal;
